import { useCallback, useMemo, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { extractApiErrorMessage } from "@/lib/api";
import { useConfirm } from "@/components/ui/dialog-imperative";
import {
  executeManualTrade,
  createPendingOrder,
} from "@/lib/api/manual-trade";
import type { SpreadData } from "@/hooks/use-live-feed";
import type { PairListItem } from "@/lib/api/pairs";
import type { PositionLive } from "@/store/dashboard.store";
import type { AccountSelection } from "./accounts-rail";
import {
  HedgeBracketPicker,
  type BracketResolved,
  type BracketState,
  type RunningPositionRef,
} from "@/components/trade/hedge-bracket-picker";
import { mirrorAbsoluteSlTp } from "@/components/trade/bracket-utils";

type Mode = "market" | "pending";

/**
 * Order panel.
 *
 * Two UI paths, both ultimately fan out through /api/manual-trade/execute
 * so per-leg `command:open` frames hit each bot independently and we get
 * per-leg D1 acks (real broker tickets or real driver errors). No
 * 3-stage HedgePair handshake is involved — that path is reserved for
 * the explicit Open-Hedge dialog on /pairs/:slug, which buys atomicity
 * at the cost of needing both bots to ack within 25 s.
 *
 * The two UI shapes:
 *
 *   1. **Hedge intent** (selection = exactly two accounts on opposite
 *      sides + market mode). The panel shows the live bid/ask strip and
 *      a side-aware SL/TP picker; SL/TP are entered ONCE from the BUY
 *      leg's perspective and the panel mirrors them client-side around
 *      the live reference price before fanning out — so the SELL leg
 *      gets prices on the right side of mid (SL above, TP below) and
 *      the broker doesn't reject either fill.
 *
 *   2. **Per-leg fan-out** (single account, same-side multi-leg, or
 *      pending mode). Plain absolute SL/TP inputs; each leg fires its
 *      own command independently. Pending mode uses /pending instead.
 *
 * SL is required in both paths (CLAUDE.md rule). The panel disables
 * the submit button until SL/TP are valid for the active path.
 */
export function OrderPanel({
  pair,
  symbol,
  selection,
  tick,
  livePositions,
  onFired,
}: {
  /** Active pair. Used for status/mode guards, contract size, and to
   *  resolve account ids in the selection back to firm names for the
   *  hedge picker's leg labels. */
  pair: PairListItem;
  symbol: string;
  selection: Map<string, AccountSelection>;
  tick: SpreadData | null;
  /** Open positions on the pair's accounts. Picker uses them to offer
   *  a "Copy SL/TP" button for matching open positions. */
  livePositions?: Record<string, PositionLive> | null;
  /** Called after a successful submit so the page can clear selection
   *  or refocus. Not called on failure — toast handles that. */
  onFired?(): void;
}): ReactNode {
  const pairId = pair.id;
  const pairStatus = pair.status;
  const pairMode = pair.mode;
  const confirm = useConfirm();
  const [mode, setMode] = useState<Mode>("market");
  const [lots, setLots] = useState("0.01");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [slippage, setSlippage] = useState("5");

  // Hedge bracket picker state. Independent from `sl`/`tp` so toggling
  // between hedge and non-hedge selections keeps each branch's input
  // intact (operator can pick a 2nd account, fill the picker, then
  // de-pick — old absolute SL/TP is still there).
  const [bracketState, setBracketState] = useState<BracketState>({
    stopLoss: "",
    takeProfit: "",
    mode: "price",
    perspective: "buy",
  });
  const [bracket, setBracket] = useState<BracketResolved>({
    ok: false,
    error: "Stop loss and take profit must be greater than zero.",
  });
  const handleResolved = useCallback((b: BracketResolved): void => setBracket(b), []);

  // Pending-specific
  const [triggerPrice, setTriggerPrice] = useState("");
  const [triggerDirection, setTriggerDirection] = useState<"ge" | "le">("ge");
  const [expiresInMin, setExpiresInMin] = useState("1440"); // 24h

  const [submitting, setSubmitting] = useState(false);

  const pickedCount = selection.size;
  const lotsNum = Number(lots);
  const slNum = Number(sl);
  const tpNum = tp.trim() === "" ? null : Number(tp);
  const slippageNum = Number(slippage);

  // Hedge intent = exactly 2 legs with opposite sides on a market open.
  // We don't auto-balance lots here; the panel's "Lots (default)" is
  // sent as the per-leg ceiling and the DO clamps it down to the safer
  // of the two free-margin reports during prepare.
  const selectionList = useMemo(() => Array.from(selection.values()), [selection]);
  const buyLeg = selectionList.find((s) => s.side === "buy") ?? null;
  const sellLeg = selectionList.find((s) => s.side === "sell") ?? null;
  const isHedgeIntent =
    mode === "market" && pickedCount === 2 && buyLeg !== null && sellLeg !== null;

  const lotsValid = Number.isFinite(lotsNum) && lotsNum > 0;
  const slippageValid = Number.isFinite(slippageNum) && slippageNum >= 1;

  const legacySlValid = Number.isFinite(slNum) && slNum > 0;
  const legacyTpValid = tpNum === null || (Number.isFinite(tpNum) && tpNum > 0);

  const baseLegacyValid = lotsValid && legacySlValid && legacyTpValid && slippageValid;

  const pendingExtraValid = useMemo(() => {
    if (mode !== "pending") return true;
    const tp0 = Number(triggerPrice);
    const exp = Number(expiresInMin);
    return (
      Number.isFinite(tp0) &&
      tp0 > 0 &&
      Number.isFinite(exp) &&
      exp > 0 &&
      exp <= 60 * 24 * 30
    );
  }, [mode, triggerPrice, expiresInMin]);

  const canSubmit = isHedgeIntent
    ? lotsValid && bracket.ok && !submitting
    : baseLegacyValid &&
      pendingExtraValid &&
      pickedCount > 0 &&
      !submitting &&
      // Pending only supports one account at a time (the row is per-leg).
      (mode === "market" || pickedCount === 1);

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    if (pairStatus === "setup") {
      toast.error("Activate the pair before placing orders.");
      return;
    }
    if (pairMode === "full_auto") {
      const ok = await confirm({
        title: "AI Is Active On This Pair",
        description:
          "Haiku is autonomously deciding on this pair right now (full_auto). Your manual trade may conflict with autonomous decisions. Continue?",
        confirmText: "Place Order",
        destructive: true,
      });
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      if (isHedgeIntent && buyLeg && sellLeg && bracket.ok) {
        // Distance mode lets the picker resolve to ok with referencePrice=null;
        // we need an absolute mid here to mirror SL/TP per leg. Refuse early
        // with a clear toast so the operator sees why submit didn't fire.
        if (bracket.referencePrice == null) {
          toast.error(
            "Live market price isn't available — can't compute mirrored SL/TP. Switch to Price mode once the quote returns.",
          );
          return;
        }
        const ref = bracket.referencePrice;
        const buyAbs = mirrorAbsoluteSlTp(
          ref,
          bracket.slDistancePrice,
          bracket.tpDistancePrice,
          "buy",
        );
        const sellAbs = mirrorAbsoluteSlTp(
          ref,
          bracket.slDistancePrice,
          bracket.tpDistancePrice,
          "sell",
        );
        const commands = [
          {
            accountId: buyLeg.accountId,
            symbol,
            side: "buy" as const,
            lots: lotsNum,
            sl: buyAbs.sl,
            tp: buyAbs.tp,
            slippage: slippageNum,
          },
          {
            accountId: sellLeg.accountId,
            symbol,
            side: "sell" as const,
            lots: lotsNum,
            sl: sellAbs.sl,
            tp: sellAbs.tp,
            slippage: slippageNum,
          },
        ];
        const res = await executeManualTrade({ pairId, commands });
        if (res.failed === 0) {
          const tickets = res.acks
            .map((a) => (a.ticket ? `#${a.ticket}` : "?"))
            .join(" / ");
          toast.success(`Hedge opened — ${tickets}`);
        } else if (res.dispatched > 0) {
          toast.warning(
            `${res.dispatched} of ${res.acks.length} hedge legs filled — ${res.failed} failed`,
          );
          for (const ack of res.acks) {
            if (!ack.ok) {
              const tail =
                ack.accountId.length > 10 ? `…${ack.accountId.slice(-6)}` : ack.accountId;
              toast.error(`${tail}: ${ack.error ?? "no_response"}`);
            }
          }
        } else {
          for (const ack of res.acks) {
            const tail =
              ack.accountId.length > 10 ? `…${ack.accountId.slice(-6)}` : ack.accountId;
            toast.error(`${tail}: ${ack.error ?? "no_response"}`);
          }
        }
        onFired?.();
      } else if (mode === "market") {
        const commands = selectionList.map((s) => ({
          accountId: s.accountId,
          symbol,
          side: s.side,
          lots: s.lotsOverride ?? lotsNum,
          sl: slNum,
          tp: tpNum,
          slippage: slippageNum,
        }));
        const res = await executeManualTrade({ pairId, commands });
        if (res.failed === 0) {
          toast.success(
            `Filled ${res.dispatched} leg${res.dispatched === 1 ? "" : "s"}`,
          );
        } else {
          if (res.dispatched > 0) {
            toast.warning(
              `${res.dispatched} of ${res.acks.length} legs filled — ${res.failed} failed`,
            );
          }
          for (const ack of res.acks) {
            if (!ack.ok) {
              const tail = ack.accountId.length > 10 ? `…${ack.accountId.slice(-6)}` : ack.accountId;
              toast.error(`${tail}: ${ack.error ?? "no_response"}`);
            }
          }
        }
        onFired?.();
      } else {
        // Pending — only one account
        const onlySel = selectionList[0]!;
        await createPendingOrder({
          pairId,
          accountId: onlySel.accountId,
          symbol,
          side: onlySel.side,
          lots: onlySel.lotsOverride ?? lotsNum,
          triggerPrice: Number(triggerPrice),
          triggerDirection,
          sl: slNum,
          tp: tpNum,
          slippage: slippageNum,
          expiresInMin: Number(expiresInMin),
        });
        toast.success(`Pending queued · trigger ${triggerDirection} ${triggerPrice}`);
        onFired?.();
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const sideOnlyBuy = selectionList.length > 0 && selectionList.every((s) => s.side === "buy");
  const sideOnlySell = selectionList.length > 0 && selectionList.every((s) => s.side === "sell");
  const sameSideMulti = mode === "market" && pickedCount >= 2 && (sideOnlyBuy || sideOnlySell);

  const firmNameFor = (accountId: string): string => {
    if (accountId === pair.longAccount.accountId) return pair.longAccount.firmName;
    if (accountId === pair.shortAccount.accountId) return pair.shortAccount.firmName;
    return formatAccountTail(accountId);
  };
  const buyLegLabel = buyLeg ? firmNameFor(buyLeg.accountId) : "—";
  const sellLegLabel = sellLeg ? firmNameFor(sellLeg.accountId) : "—";

  const runningPositions = useMemo<RunningPositionRef[]>(() => {
    if (!livePositions || !buyLeg || !sellLeg) return [];
    const out: RunningPositionRef[] = [];
    for (const p of Object.values(livePositions)) {
      const isBuyLegAcct = p.accountId === buyLeg.accountId;
      const isSellLegAcct = p.accountId === sellLeg.accountId;
      if (!isBuyLegAcct && !isSellLegAcct) continue;
      out.push({
        accountId: p.accountId,
        legSide: isBuyLegAcct ? "buy" : "sell",
        positionSide: p.side,
        ticket: p.ticket,
        symbol: p.symbol,
        lots: p.lots,
        entry: p.entry,
        stopLoss: p.stopLoss,
        takeProfit: p.takeProfit,
        legLabel: firmNameFor(p.accountId),
      });
    }
    return out;
  }, [livePositions, buyLeg, sellLeg]);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card/60">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          Order
        </span>
        <div
          role="radiogroup"
          aria-label="Order mode"
          className="grid grid-cols-2 overflow-hidden rounded-md border border-border text-[11px]"
        >
          {(["market", "pending"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              style={{ touchAction: "manipulation" }}
              className={cn(
                "px-3 py-1 font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                mode === m
                  ? "bg-accent/15 text-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {m.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3 overflow-y-auto p-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Lots (default)">
            <Input
              value={lots}
              onChange={(e) => setLots(e.target.value)}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              className="h-8 font-mono text-sm tabular-nums"
            />
          </Field>
          <Field label="Slippage (pts)">
            <Input
              value={slippage}
              onChange={(e) => setSlippage(e.target.value)}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              className="h-8 font-mono text-sm tabular-nums"
            />
          </Field>
        </div>

        <AnimatePresence initial={false} mode="wait">
          {isHedgeIntent ? (
            <motion.div
              key="hedge-bracket"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.16 }}
              className="grid gap-3"
            >
              <HedgeBracketPicker
                symbol={symbol}
                buyLegLabel={buyLegLabel}
                sellLegLabel={sellLegLabel}
                value={bracketState}
                onChange={setBracketState}
                onResolved={handleResolved}
                autoFillOnFirstQuote
                liveQuote={tick}
                contractSize={pair.contractSize ?? null}
                runningPositions={runningPositions}
              />
              {selectionList.some((s) => s.lotsOverride != null) && (
                <p
                  aria-live="polite"
                  className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-600 dark:text-amber-400"
                >
                  Per-leg lots overrides are ignored for hedge market opens — both legs
                  share the panel’s Lots value above.
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="legacy-bracket"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.16 }}
              className="grid grid-cols-2 gap-2"
            >
              <Field label="Stop Loss (abs)" required>
                <Input
                  value={sl}
                  onChange={(e) => setSl(e.target.value)}
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="required"
                  className={cn(
                    "h-8 font-mono text-sm tabular-nums",
                    sl.trim() === "" && "border-destructive/50",
                  )}
                />
              </Field>
              <Field label="Take Profit (abs)">
                <Input
                  value={tp}
                  onChange={(e) => setTp(e.target.value)}
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="none"
                  className="h-8 font-mono text-sm tabular-nums"
                />
              </Field>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {mode === "pending" && (
            <motion.div
              key="pending-extras"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Field label="Trigger Price">
                  <Input
                    value={triggerPrice}
                    onChange={(e) => setTriggerPrice(e.target.value)}
                    inputMode="decimal"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={tick?.bid.toFixed(tick.digits ?? 5) ?? "…"}
                    className="h-8 font-mono text-sm tabular-nums"
                  />
                </Field>
                <Field label="When">
                  <div
                    role="radiogroup"
                    aria-label="Trigger direction"
                    className="grid grid-cols-2 overflow-hidden rounded-md border border-border text-[11px]"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={triggerDirection === "ge"}
                      onClick={() => setTriggerDirection("ge")}
                      style={{ touchAction: "manipulation" }}
                      className={cn(
                        "px-2 py-1 font-mono focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                        triggerDirection === "ge"
                          ? "bg-accent/15 text-foreground"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      bid ≥
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={triggerDirection === "le"}
                      onClick={() => setTriggerDirection("le")}
                      style={{ touchAction: "manipulation" }}
                      className={cn(
                        "px-2 py-1 font-mono focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                        triggerDirection === "le"
                          ? "bg-accent/15 text-foreground"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      ask ≤
                    </button>
                  </div>
                </Field>
                <Field label="Expires (min)">
                  <Input
                    value={expiresInMin}
                    onChange={(e) => setExpiresInMin(e.target.value)}
                    inputMode="decimal"
                    className="h-8 font-mono text-sm tabular-nums"
                  />
                </Field>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col gap-1 rounded-lg border border-border/60 bg-muted/20 p-2 text-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Selected</span>
            <span className="font-mono tabular-nums text-foreground">
              {pickedCount}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Direction mix</span>
            <span className="flex gap-1">
              {selectionList.some((s) => s.side === "buy") && (
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-500">
                  BUY
                </Badge>
              )}
              {selectionList.some((s) => s.side === "sell") && (
                <Badge variant="outline" className="border-rose-500/30 text-rose-500">
                  SELL
                </Badge>
              )}
              {pickedCount === 0 && (
                <span className="text-muted-foreground">—</span>
              )}
            </span>
          </div>
          {isHedgeIntent && (
            <p className="pt-1 text-[10px] text-emerald-600 dark:text-emerald-400">
              Hedge mirror active — SL/TP set on the BUY leg are mirrored
              around the live mid for the SELL leg before each leg fires.
            </p>
          )}
          {sameSideMulti && (
            <p className="pt-1 text-[10px] text-amber-500">
              Same-direction multi-leg — no mirror; each leg fires its own absolute SL/TP.
            </p>
          )}
          {mode === "pending" && pickedCount > 1 && (
            <p className="pt-1 text-[10px] text-amber-500">
              Pending mode only supports ONE account — pick one leg.
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-border/60 p-3">
        <Button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          aria-busy={submitting}
          className={cn(
            "h-10 w-full text-sm font-semibold",
            isHedgeIntent && "bg-accent text-accent-foreground hover:bg-accent/90",
            !isHedgeIntent &&
              mode === "market" &&
              sideOnlyBuy &&
              "bg-emerald-500 hover:bg-emerald-500/90 text-white",
            !isHedgeIntent &&
              mode === "market" &&
              sideOnlySell &&
              "bg-rose-500 hover:bg-rose-500/90 text-white",
          )}
          style={{ touchAction: "manipulation" }}
        >
          {submitting && (
            <Loader2 aria-hidden="true" className="mr-2 inline h-4 w-4 animate-spin" />
          )}
          {submitting
            ? "Sending…"
            : isHedgeIntent
              ? "Open Hedge"
              : mode === "market"
                ? "Execute"
                : "Queue Pending"}
        </Button>
        {!isHedgeIntent && !baseLegacyValid && (
          <p className="pt-2 text-center text-[10px] text-destructive">
            SL is required (per-trade rule).
          </p>
        )}
        {isHedgeIntent && !bracket.ok && (
          <p className="pt-2 text-center text-[10px] text-destructive">
            {bracket.error}
          </p>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}

function formatAccountTail(accountId: string): string {
  return accountId.length > 10 ? `…${accountId.slice(-6)}` : accountId;
}
